/**
 * Phase 9.27: Invoice Item Row with Split Editing Behavior
 * Phase 9.34: Added quantity/rate display and editing
 *
 * Editing behavior is split by field type:
 * - Description: Updates the billable item and the explicitly selected invoice lines together
 * - Amount: Updates invoice_items → only affects THIS invoice (supports partial invoicing)
 * - Quantity/Rate: Updates invoice_items → only affects THIS invoice (display on PDF)
 */
import {
  changeButtonLabel,
  changeableOthers,
  clientHasOldWording,
  defaultTickedIds,
  descriptionEditRequest,
  postSendAcknowledgementIds,
  otherInvoiceStatusLabel,
  otherInvoicesHint,
  type OtherInvoiceOnItem,
} from '@/lib/sharedItemEdit';
import { Checkbox } from '@/components/ui/checkbox';
import { useState } from 'react';
import { TableRow, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, Check, X, AlertTriangle, Info, Paperclip, DollarSign } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { useUpdateBillableItem, useUpdateInvoiceItem } from '@/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateProjectListQueries, invoiceListQueryFilter } from '@/lib/cache-utils';
import { getSuggestedTaxRate } from '@/lib/taxRates';
import { deriveAmountFromQtyRate } from '@/lib/qtyRateAmount';
import {
  joinInvoiceNumbers,
  lowerOutcome,
  otherInvoicesBilled,
  type LowerChoice,
} from '@/lib/lowerItemTotal';
import { LowerItemTotalChoice } from './LowerItemTotalChoice';
import {
  TAX_CLASS_OPTIONS,
  TAX_CLASS_UNSET,
  taxClassLabel,
  isTaxClassOverridden,
} from '@/lib/taxClasses';

/**
 * v0.14.4 R2: round a derived tax amount to 2 decimal places so the line
 * matches the backend's own derivation (models/invoice_items.py:454). The
 * pre-existing amount editor sent the raw unrounded product, which drifted by
 * sub-cent amounts from the server total.
 */
function roundTaxAmount(amount: number, ratePercent: number): number {
  return Math.round(amount * (ratePercent / 100) * 100) / 100;
}


interface InvoiceItemRowProps {
  item: {
    id: string;
    billable_item_id: string | null;
    description: string;
    amount: number;
    tax_rate: number;
    tax_amount: number;
    invoice_count?: number;
    other_invoices?: OtherInvoiceOnItem[];
    // What other lines on this same invoice bill on the item (the server's lowering rule counts them). Absent = unknown.
    same_invoice_other_billed?: number;
    billable_item_category?: string | null;
    // Phase 9.27: Added for split behavior
    billable_item_description?: string;
    billable_item_amount?: number;
    // Phase 9.34: Quantity/rate fields for PDF display
    quantity?: number | null;
    unit_price?: number | null;
    unit_type?: 'hours' | 'days' | 'units' | 'each' | null;
    // Phase 10b.2: Receipt file count (from parent query)
    receipt_count?: number;
    // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC30/AC31): the line's tax class
    // (from its billable item) and the project-category default it would have
    // inherited. Shown per line; editable per item; a DIFFERENCE from the
    // category default is badged as a hand override.
    tax_class?: string | null;
    category_default_tax_class?: string | null;
  };
  invoiceId: string;
  invoiceStatus: 'draft' | 'ready' | 'sent' | 'paid' | 'cancelled' | 'overdue';
  /** True when this invoice's own send evidence says sent (a Ready invoice with a recorded send): its edit is acknowledged. */
  invoiceSendRecorded?: boolean;
  onRemove: (itemId: string) => void;
  // Phase 9.18b: Show category column for service/expense distinction
  showCategory?: boolean;
}

type EditMode = 'none' | 'description' | 'amount';

export function InvoiceItemRow({
  item,
  invoiceId,
  invoiceStatus,
  invoiceSendRecorded = false,
  onRemove,
}: InvoiceItemRowProps) {
  const [editMode, setEditMode] = useState<EditMode>('none');
  // Phase 9.27: Use billable_item_description for display (falls back to description for backward compat)
  const displayDescription = item.billable_item_description ?? item.description;
  const [editDescription, setEditDescription] = useState(displayDescription);
  const [editAmount, setEditAmount] = useState(item.amount);
  // Phase 9.34: State for quantity/rate editing
  const [editQuantity, setEditQuantity] = useState<string>(item.quantity?.toString() ?? '');
  const [editUnitPrice, setEditUnitPrice] = useState<string>(item.unit_price?.toString() ?? '');
  const [editUnitType, setEditUnitType] = useState<string>(item.unit_type ?? '');
  // v0.14.4 R2: tax rate is edited as a string so an empty field (keep current
  // rate) stays distinguishable from an explicit "0" (override to no tax).
  const [editTaxRate, setEditTaxRate] = useState<string>(item.tax_rate?.toString() ?? '');
  const queryClient = useQueryClient();
  const updateBillableItem = useUpdateBillableItem();
  const updateInvoiceItem = useUpdateInvoiceItem();

  const isPaid = invoiceStatus === 'paid';
  // v0.14 X1 (2026-05-29): mirror backend invoice_not_editable_for_items guard.
  // Line-item edits are only allowed in draft or ready statuses — sent / paid /
  // partially_paid / cancelled invoices are immutable to keep the PDF +
  // invoice_payments ledger in sync with the rendered total. Pre-fix the UI
  // exposed edit/remove on sent and partially_paid because it only blocked
  // `paid`; backend now returns 409 invoice_not_editable_for_items on those, so
  // hide the affordance to match.
  const isStatusEditable = invoiceStatus === 'draft' || invoiceStatus === 'ready';
  const canEdit = isStatusEditable && item.billable_item_id;
  const isOnMultipleInvoices = (item.invoice_count ?? 1) > 1;
  // R5 AC4: the other invoices that carry this item (never a cancelled one). A description edit asks which of them change.
  const otherInvoices = isOnMultipleInvoices ? changeableOthers(item.other_invoices) : [];
  const hasOtherInvoices = otherInvoices.length > 0;
  const otherInvoiceHint = isOnMultipleInvoices
    ? otherInvoicesHint(hasOtherInvoices ? otherInvoices.length : (item.invoice_count ?? 1) - 1)
    : null;
  const [choosingInvoices, setChoosingInvoices] = useState(false);
  const [tickedInvoiceIds, setTickedInvoiceIds] = useState<string[]>([]);
  // Terminal cycle 2 LOW: the server refused because an invoice was sent after this page loaded.
  const [ackNotice, setAckNotice] = useState<string | null>(null);

  // Phase 9.27: Check if this is a partial invoice (invoice amount < billable item amount)
  const billableTotal = item.billable_item_amount ?? item.amount;
  const isPartialInvoice = item.amount < billableTotal;

  // Christa 2026-09-25 (feedback 9725dd36): lowering a $1,500 line to $1,250 meant "the item is $1,250". Leaving the
  // item at $1,500 printed "Remaining $250" on the PDF, which she did not want. But a quick-added $10,000 retainer
  // lowered to $2,000 usually means "bill $2,000 now". So when a line is lowered below its item, she must choose; nothing
  // is preselected and Save waits for the choice. The server sets the new total to this line plus what other live
  // invoices bill, so it can never go below them; the labels use the same rule.
  const otherInvoicesOnItem = Math.max((item.invoice_count ?? 1) - 1, 0);
  // Same rule as the server: every other live line on the item, on other invoices and on this one.
  const otherInvoicesTotal = otherInvoicesBilled(otherInvoices, otherInvoicesOnItem);
  const otherBilled =
    otherInvoicesTotal === null || typeof item.same_invoice_other_billed !== 'number'
      ? null
      : Math.round((otherInvoicesTotal + item.same_invoice_other_billed) * 100) / 100;
  const outcome = lowerOutcome(editAmount, billableTotal, otherBilled);
  const needsLowerChoice =
    !!item.billable_item_id &&
    item.billable_item_amount !== undefined &&
    editAmount < item.amount &&
    (outcome.remainder === null || outcome.remainder > 0.005);
  const [lowerChoice, setLowerChoice] = useState<LowerChoice | null>(null);
  const lowerItemTotal = needsLowerChoice && lowerChoice === 'lower';
  const lowerChoiceMissing = needsLowerChoice && lowerChoice === null;
  const otherInvoiceNames = otherInvoicesOnItem > 0 ? joinInvoiceNumbers(otherInvoices) : null;
  const lowerChoiceNote = otherInvoicesOnItem === 0
    ? null
    : otherInvoiceNames
      ? `${otherInvoiceNames} also ${otherInvoicesOnItem === 1 ? 'bills' : 'bill'} this item.`
      : `${otherInvoicesOnItem} other invoice${otherInvoicesOnItem === 1 ? '' : 's'} also bill this item.`;

  // Changing quantity or rate fills the amount, same as the Add Item form. Typing in Amount itself stays an override.
  const handleQuantityChange = (value: string) => {
    setEditQuantity(value);
    const derived = deriveAmountFromQtyRate(value, editUnitPrice);
    if (derived !== null) setEditAmount(derived);
  };
  const handleUnitPriceChange = (value: string) => {
    setEditUnitPrice(value);
    const derived = deriveAmountFromQtyRate(editQuantity, value);
    if (derived !== null) setEditAmount(derived);
  };

  // The shared endpoint updates the billable item and exactly the selected invoice lines in one transaction.
  // A separate line PUT could commit after that transaction refuses, leaving a partial correction behind.
  // Optimistic: update cache + exit edit mode immediately, rollback on error.
  const handleSaveDescription = async () => {
    if (!item.billable_item_id || !canEdit) return;
    // R5 AC4: when the item is on another live invoice, the check opens the list of those invoices instead of saving.
    // She chooses which ones change; the named button below the list is what saves.
    if (hasOtherInvoices && !choosingInvoices) {
      setTickedInvoiceIds(defaultTickedIds(otherInvoices));
      setAckNotice(null);
      setChoosingInvoices(true);
      return;
    }
    const request = descriptionEditRequest(
      editDescription, invoiceId, hasOtherInvoices ? tickedInvoiceIds : [], otherInvoices, invoiceSendRecorded,
    );
    setChoosingInvoices(false);

    // 0. Stop any in-flight refetch of the caches we are about to overwrite.
    // Without this a refetch already on the wire can resolve AFTER the
    // optimistic write and clobber it, or — worse — land between the snapshot
    // and a rollback, so the restore puts stale data back over fresher server
    // state. Standard React Query optimistic recipe; it was missing here.
    await queryClient.cancelQueries({ queryKey: ['invoices', invoiceId, 'full'] });

    // 1. Snapshot for rollback
    const previousFull = queryClient.getQueryData(['invoices', invoiceId, 'full']);

    // 2. Optimistic cache update (synchronous — UI updates immediately)
    queryClient.setQueryData(['invoices', invoiceId, 'full'], (old: any) => {
      if (!old) return old;
      const updatedItems = (old.invoice_items || []).map((i: any) =>
        i.id === item.id
          ? { ...i, description: editDescription, billable_item_description: editDescription }
          : i
      );
      return { ...old, invoice_items: updatedItems };
    });

    // 3. Exit edit mode immediately (user sees the change right away)
    setEditMode('none');

    try {
      // 4. One accepted operation saves both the source item and its selected printed lines.
      await updateBillableItem.mutateAsync({
        id: item.billable_item_id,
        data: request,
      });

      // 5. Preserve this invoice's optimistic detail, but refresh the sibling lines this operation changed.
      queryClient.invalidateQueries({
        queryKey: ['invoices'],
        predicate: ({ queryKey }) => {
          if (!(queryKey.length === 3 && queryKey[2] === 'full')) return true;
          return typeof queryKey[1] === 'string' && queryKey[1] !== invoiceId
            && request.description_invoice_ids.includes(queryKey[1]);
        },
      });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      invalidateProjectListQueries(queryClient);
    } catch (error) {
      // 6. Rollback: restore cache + re-enter edit mode
      if (previousFull) {
        queryClient.setQueryData(['invoices', invoiceId, 'full'], previousFull);
      }
      setEditMode('description');
      const sentSince = postSendAcknowledgementIds(error);
      if (sentSince !== null) {
        // An invoice changed to sent after the page loaded. Say so in plain words, untick what the server named, reload
        // the other-invoices list (its labels may be stale), and open the selection again so she chooses with the truth.
        const numbers = sentSince.map((id) => otherInvoices.find((other) => other.invoice_id === id)?.invoice_number)
          .filter((number): number is string => !!number);
        const namedInvoices = numbers.length > 1
          ? `Invoices ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]} were`
          : `Invoice ${numbers[0]} was`;
        setAckNotice(sentSince.includes(invoiceId)
          ? 'This invoice was sent after this page opened. Your change was not saved.'
          : numbers.length > 0
            ? `${namedInvoices} sent after this page opened. Check the list and choose again.`
            : 'An invoice was sent after this page opened. Check the list and choose again.');
        setTickedInvoiceIds((prior) => prior.filter((id) => !sentSince.includes(id)));
        setChoosingInvoices(hasOtherInvoices);
        queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId, 'full'] });
      }
    }
  };

  // Phase 9.27: Save amount (only affects this invoice)
  // Phase 9.34: Also saves quantity/rate fields
  // Optimistic: update cache + exit edit mode immediately, rollback on error.
  //
  // When editAmount exceeds billable_item_amount, we update the billable item
  // amount first (non-optimistic await), then the invoice line. Backend validator
  // rejects invoice line amount > billable_item.total - other_invoiced; raising
  // the cap is the only way to allow the larger line.
  // newCap formula preserves headroom used by other invoice lines on the same
  // billable item; overshoots slightly when there's unused remaining (benign).
  const handleSaveAmount = async () => {
    const isOverBillableAmount =
      item.billable_item_amount !== undefined && editAmount > billableTotal;
    const newBillableItemAmount = editAmount + (billableTotal - item.amount);
    // Save waits for her answer when the line is lowered below its item.
    if (lowerChoiceMissing && !isOverBillableAmount) return;
    const lowering = lowerItemTotal && !isOverBillableAmount;
    // Only when the new total is known exactly; otherwise the server's answer fills it in below.
    const optimisticLoweredTotal = lowering && outcome.exact ? outcome.newTotal : null;

    if (isOverBillableAmount && item.billable_item_id) {
      try {
        await updateBillableItem.mutateAsync({
          id: item.billable_item_id,
          data: { amount: newBillableItemAmount },
        });
      } catch (error) {
        // Stay in edit mode; don't proceed to invoice line update.
        return;
      }
    }

    // Phase 9.34: Parse quantity/rate fields
    const quantity = editQuantity ? parseFloat(editQuantity) : null;
    const unitPrice = editUnitPrice ? parseFloat(editUnitPrice) : null;
    // Narrow the <select> string back to the unit_type enum (empty → null).
    // The select's options are exactly these values, so the membership check is
    // total; this keeps the typed mutation payload sound without unsafe casts.
    const UNIT_TYPES = ['hours', 'days', 'units', 'each'] as const;
    const unitType = (UNIT_TYPES as readonly string[]).includes(editUnitType)
      ? (editUnitType as (typeof UNIT_TYPES)[number])
      : null;

    // v0.14.4 R2: resolve the effective tax rate. Empty input keeps the current
    // rate (explicit "0" is honoured as an override because we test the trimmed
    // string, not falsiness). Out-of-range values are rejected here so the
    // mutation never fires with a bad rate.
    const trimmedRate = editTaxRate.trim();
    const parsedRate = trimmedRate === '' ? item.tax_rate : parseFloat(trimmedRate);
    if (Number.isNaN(parsedRate) || parsedRate < 0 || parsedRate > 100) {
      // Stay in edit mode; surface nothing destructive. The input's own
      // min/max/step also block these via the browser, this is the JS guard.
      return;
    }
    const newTaxRate = parsedRate;
    // Round to 2dp to match the backend derivation (models/invoice_items.py:454);
    // the pre-existing editor sent the raw unrounded product (was bug at :172).
    const newTaxAmount = roundTaxAmount(editAmount, newTaxRate);

    // 0. Stop in-flight refetches of BOTH cache families before snapshotting —
    // the list caches as well as the detail, since step 2b writes to both. A
    // refetch resolving mid-flight otherwise either clobbers the optimistic
    // write or makes the rollback restore stale rows over newer server data.
    await Promise.all([
      queryClient.cancelQueries({ queryKey: ['invoices', invoiceId, 'full'] }),
      queryClient.cancelQueries(invoiceListQueryFilter),
    ]);

    // 1. Snapshot for rollback
    const previousFull = queryClient.getQueryData(['invoices', invoiceId, 'full']);
    // v0.14.4 R5 (silent-loss kill) applied here 2026-08-03: snapshot EVERY
    // invoice LIST cache too, not just the detail cache. Step 2b below writes
    // optimistic fee/tax/total into every list query via setQueriesData, but
    // the catch block only ever restored the detail key — so a rejected
    // PUT /invoice-items left the list rows showing the amount the server
    // refused, indefinitely (the success-only invalidations at step 5 never
    // run on the failure path, so nothing refetched it away either). Same
    // shape, same fix as useInvoices.ts `useUpdateInvoiceInline`'s
    // previousLists. Uses the SAME filter as the write so the snapshot set and
    // the write set can never drift apart.
    const previousLists = queryClient.getQueriesData(invoiceListQueryFilter);

    // 2. Optimistic cache update (synchronous — UI updates immediately)
    queryClient.setQueryData(['invoices', invoiceId, 'full'], (old: any) => {
      if (!old) return old;
      const updatedItems = (old.invoice_items || []).map((i: any) => {
        if (i.id === item.id) {
          return {
            ...i,
            amount: editAmount,
            tax_rate: newTaxRate,
            tax_amount: newTaxAmount,
            quantity,
            unit_price: unitPrice,
            unit_type: unitType,
            ...(isOverBillableAmount
              ? { billable_item_amount: newBillableItemAmount }
              : {}),
            ...(optimisticLoweredTotal !== null
              ? { billable_item_amount: optimisticLoweredTotal }
              : {}),
          };
        }
        // When raising the billable item amount, sync sibling rows on the same
        // billable item so future cap math in this view stays accurate.
        if (
          isOverBillableAmount &&
          item.billable_item_id &&
          i.billable_item_id === item.billable_item_id
        ) {
          return { ...i, billable_item_amount: newBillableItemAmount };
        }
        return i;
      });
      const feeAmount = updatedItems.reduce((sum: number, i: any) => sum + (i.amount || 0), 0);
      const taxAmount = updatedItems.reduce((sum: number, i: any) => sum + (i.tax_amount || 0), 0);
      return {
        ...old,
        invoice_items: updatedItems,
        invoice: {
          ...old.invoice,
          fee_amount: feeAmount,
          tax_amount: taxAmount,
          total_amount: feeAmount + taxAmount,
        },
      };
    });

    // 2b. Also update invoice list cache so navigating back shows correct totals.
    // Snapshotted above as `previousLists` — keep the two in lockstep.
    queryClient.setQueriesData(
      invoiceListQueryFilter,
      (old: any) => {
        if (!old?.invoices) return old;
        const detailData = queryClient.getQueryData(['invoices', invoiceId, 'full']) as any;
        if (detailData?.invoice) {
          return {
            ...old,
            invoices: old.invoices.map((inv: any) =>
              inv.id === invoiceId
                ? {
                    ...inv,
                    fee_amount: detailData.invoice.fee_amount,
                    tax_amount: detailData.invoice.tax_amount,
                    total_amount: detailData.invoice.total_amount,
                  }
                : inv
            ),
          };
        }
        return old;
      }
    );

    // 3. Exit edit mode immediately (user sees the change right away)
    setEditMode('none');

    try {
      // 4. Fire mutation (async — UI already updated)
      const response = await updateInvoiceItem.mutateAsync({
        id: item.id,
        data: {
          amount: editAmount,
          // v0.14.4 R2: send the resolved rate + rounded tax so the line
          // matches the backend derivation. Backend accepts both
          // (routers/invoice_items.py:290).
          tax_rate: newTaxRate,
          tax_amount: newTaxAmount,
          quantity,
          unit_price: unitPrice,
          unit_type: unitType,
          ...(lowering ? { lower_billable_item_total: true } : {}),
        },
      });
      setLowerChoice(null);

      // 4b. The server says what the item total became; every row on this item shows it.
      const loweredTotal = (response as { invoice_item?: { billable_item_amount?: number | string | null } } | undefined)
        ?.invoice_item?.billable_item_amount;
      if (lowering && loweredTotal != null && item.billable_item_id) {
        const total = Number(loweredTotal);
        type DetailLine = { billable_item_id?: string | null; billable_item_amount?: number };
        queryClient.setQueryData(
          ['invoices', invoiceId, 'full'],
          (old: { invoice_items?: DetailLine[] } | undefined) => {
            if (!old) return old;
            return {
              ...old,
              invoice_items: (old.invoice_items || []).map((i) =>
                i.billable_item_id === item.billable_item_id ? { ...i, billable_item_amount: total } : i
              ),
            };
          }
        );
      }

      // 5. Background invalidations — exclude detail query (already set via setQueryData)
      queryClient.invalidateQueries({
        queryKey: ['invoices'],
        predicate: (query) => !(query.queryKey.length === 3 && query.queryKey[2] === 'full'),
      });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      invalidateProjectListQueries(queryClient);
    } catch (error) {
      // 6. Rollback: restore EVERY cache written in step 2 + re-enter edit mode
      // with the values the user typed.
      if (previousFull) {
        queryClient.setQueryData(['invoices', invoiceId, 'full'], previousFull);
      }
      // The list restore is deliberately NOT gated on `previousFull` — the two
      // snapshots are independent and a write must be undone by ITS OWN
      // snapshot, never by a sibling's. Today the step-2b updater happens to
      // read the detail cache to source the new totals, so with no detail cache
      // it no-ops and there is nothing to undo; that coupling is incidental,
      // not a contract. The moment the totals are derived locally instead
      // (they already are, one scope up — `feeAmount`/`taxAmount`), a
      // `previousFull`-gated restore would silently strand rejected totals on
      // the list rows. Mirrors useInvoices.ts `useUpdateInvoiceInline`, whose
      // `previousLists` restore is likewise ungated. Restoring an unchanged
      // cache is a no-op, so the ungated form costs nothing.
      previousLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      setEditMode('amount');
    }
  };

  const handleCancel = () => {
    setChoosingInvoices(false);
    setEditDescription(displayDescription);
    setEditAmount(item.amount);
    // Phase 9.34: Reset quantity/rate state
    setEditQuantity(item.quantity?.toString() ?? '');
    setEditUnitPrice(item.unit_price?.toString() ?? '');
    setEditUnitType(item.unit_type ?? '');
    // v0.14.4 R2: reset tax-rate edit state too.
    setEditTaxRate(item.tax_rate?.toString() ?? '');
    setLowerChoice(null);
    setEditMode('none');
  };

  // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC30): per-item tax-class change —
  // Jamie's "manual override to change per item". The class lives on the
  // billable item, so: 1) PATCH the billable item's tax_class, 2) re-save the
  // invoice line (unchanged amount/rate) so the backend's rules-mode recompute
  // runs against the new class, 3) invalidate so the refreshed taxes render.
  // In legacy mode both writes are no-op-equivalent (same values recomputed),
  // so the control is inert until the tenant is flipped.
  const handleTaxClassChange = async (next: string) => {
    if (!item.billable_item_id) return;
    try {
      await updateBillableItem.mutateAsync({
        id: item.billable_item_id,
        data: { tax_class: next === TAX_CLASS_UNSET ? null : next },
      });
      await updateInvoiceItem.mutateAsync({
        id: item.id,
        data: { amount: item.amount, tax_rate: item.tax_rate },
      });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    } catch {
      // hooks toast the error; the select snaps back on refetch/rollback.
    }
  };

  // AC31: a line whose class differs from its project-category default was
  // set by hand — badge it so Christa can see at a glance which lines she
  // has touched.
  const taxClassOverridden = isTaxClassOverridden(
    item.tax_class,
    item.category_default_tax_class,
    item.billable_item_category,
  );

  // The "Manual" badge, shared by the edit cell and the read-only cell so
  // the two views cannot disagree about which lines were hand-set.
  const taxClassBadge = taxClassOverridden ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800"
                data-testid={`tax-class-override-badge-${item.id}`}
              >
                Manual
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Hand-set tax class.</p>
              <p>
                Category default:{' '}
                {item.category_default_tax_class
                  ? taxClassLabel(item.category_default_tax_class)
                  : 'not set'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
  ) : null;

  // AC30/AC31 in the NORMAL view, 2026-09-13. The class used to render only
  // inside the two edit rows, so in the view Christa actually reads a
  // hand-set line looked identical to every other line -- found by the v0.17
  // preview click-through. A line with no class that carries no tax shows a
  // dash: "Unclassified" on a 0% pass-through line reads as a claim
  // that GST applies. This still drives ONLY the read-only cell below --
  // v0.17 taxclass-light dropped the equivalent nuance from the EDIT select's
  // sentinel option (now a plain "Unclassified"): the PST/GST detail lives in
  // the ⓘ tooltip on the column header now, not spelled out per-option.
  const lineCarriesNoTax = !item.tax_class && Number(item.tax_rate) === 0;

  const taxClassCell = (
    <div className="flex flex-col items-start gap-1">
      {canEdit ? (
        <select
          value={item.tax_class ?? TAX_CLASS_UNSET}
          onChange={(e) => handleTaxClassChange(e.target.value)}
          disabled={updateBillableItem.isPending || updateInvoiceItem.isPending}
          aria-label={`Tax class for ${displayDescription}`}
          data-testid={`tax-class-select-${item.id}`}
          className="h-7 w-44 rounded-md border border-input bg-background px-1.5 text-xs"
        >
          <option value={TAX_CLASS_UNSET}>Unclassified</option>
          {TAX_CLASS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <span className="text-xs" data-testid={`tax-class-label-${item.id}`}>
          {taxClassLabel(item.tax_class)}
        </span>
      )}
      {taxClassBadge}
    </div>
  );

  const taxClassReadOnlyCell = (
    <div className="flex flex-col items-start gap-1">
      <span
        className="text-xs text-muted-foreground"
        data-testid={`tax-class-display-${item.id}`}
        title={lineCarriesNoTax ? 'No tax on this line' : undefined}
      >
        {lineCarriesNoTax ? '—' : taxClassLabel(item.tax_class)}
      </span>
      {taxClassBadge}
    </div>
  );
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editMode === 'description') {
        handleSaveDescription();
      } else if (editMode === 'amount') {
        handleSaveAmount();
      }
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Phase 9.27: Editing description
  if (editMode === 'description') {
    return (
      <TableRow>
        <TableCell>
          <div className="space-y-1">
            <Input
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              data-testid="edit-description-input"
              className="w-full"
              readOnly={choosingInvoices}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {otherInvoiceHint ?? 'Updates the billable item description'}
            </p>
            {ackNotice && (
              <p className="text-sm text-amber-800" role="alert" data-testid="ack-required-notice">{ackNotice}</p>
            )}
            {choosingInvoices && (
              <div className="mt-2 space-y-2 rounded border p-3" data-testid="other-invoices-list">
                <p className="text-sm font-medium">Other invoices with this item</p>
                <ul className="space-y-2">
                  {otherInvoices.map((other) => {
                    const label = otherInvoiceStatusLabel(other);
                    const number = other.invoice_number ?? other.invoice_id;
                    const checkboxId = `other-invoice-tick-${other.invoice_id}`;
                    return (
                      <li key={other.invoice_id} className="flex items-start gap-2 text-sm"
                          data-testid={`other-invoice-${other.invoice_id}`}>
                        <Checkbox
                          id={checkboxId}
                          aria-label={`Also change invoice ${number}`}
                          checked={tickedInvoiceIds.includes(other.invoice_id)}
                          onCheckedChange={(checked) => setTickedInvoiceIds((prior) => checked === true
                            ? [...new Set([...prior, other.invoice_id])]
                            : prior.filter((id) => id !== other.invoice_id))}
                        />
                        <label htmlFor={checkboxId} className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-medium">Invoice {number}</span>
                          <span className="text-muted-foreground">{label}</span>
                          {clientHasOldWording(other) && (
                            <span className="w-full text-xs text-muted-foreground">
                              The client already has the old wording.
                            </span>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={handleSaveDescription}
                    disabled={updateBillableItem.isPending || !canEdit}
                    data-testid="change-invoices-button"
                  >
                    {changeButtonLabel(1 + tickedInvoiceIds.filter((id) =>
                      otherInvoices.some((other) => other.invoice_id === id)).length)}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setChoosingInvoices(false)}
                    data-testid="back-to-editing-button"
                  >
                    Back to editing
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right">
          ${item.amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </TableCell>
        <TableCell className="text-right">{item.tax_rate.toFixed(2)}%</TableCell>
        <TableCell>{taxClassCell}</TableCell>
        <TableCell className="text-right">
          ${item.tax_amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSaveDescription}
              disabled={updateBillableItem.isPending || choosingInvoices || !canEdit}
              data-testid="save-description-button"
              className="h-8 w-8 p-0"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              data-testid="cancel-edit-button"
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  // Phase 9.27: Editing amount
  // Phase 9.34: Also editing quantity/rate
  if (editMode === 'amount') {
    // v0.14.4 R2: derive the live tax from the edited rate. An empty input means
    // "keep current rate", so the live preview uses item.tax_rate in that case.
    const trimmedRate = editTaxRate.trim();
    const parsedRate = trimmedRate === '' ? item.tax_rate : parseFloat(trimmedRate);
    const effectiveRate = Number.isNaN(parsedRate) ? item.tax_rate : parsedRate;
    const isRateInRange = effectiveRate >= 0 && effectiveRate <= 100;
    const calculatedTax = roundTaxAmount(editAmount, effectiveRate);
    // The category-suggested rate is shown as a placeholder/hint only when it
    // differs from the line's current rate (R1 shared util).
    const suggestedRate = getSuggestedTaxRate(item.billable_item_category);
    const showSuggestion = suggestedRate !== item.tax_rate;
    const isOverBillableAmount =
      item.billable_item_amount !== undefined && editAmount > billableTotal;
    const newBillableItemAmount = editAmount + (billableTotal - item.amount);
    const otherInvoiceCount = (item.invoice_count ?? 1) - 1;
    return (
      <TableRow>
        <TableCell>
          <div className="space-y-2">
            <div>{displayDescription}</div>
            {/* Phase 9.34: Quantity/rate inputs */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Qty/Rate:</span>
              <Input
                type="number"
                value={editQuantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                placeholder="Qty"
                className="w-16 h-7 text-xs"
                step="0.0001"
                min="0"
                data-testid="edit-quantity-input"
              />
              <span className="text-muted-foreground">×</span>
              <div className="flex items-center">
                <span className="text-muted-foreground mr-1">$</span>
                <Input
                  type="number"
                  value={editUnitPrice}
                  onChange={(e) => handleUnitPriceChange(e.target.value)}
                  placeholder="Price"
                  className="w-20 h-7 text-xs"
                  step="0.01"
                  min="0"
                  data-testid="edit-unit-price-input"
                />
              </div>
              <select
                value={editUnitType}
                onChange={(e) => setEditUnitType(e.target.value)}
                className="h-7 text-xs rounded-md border border-input bg-background px-2"
                data-testid="edit-unit-type-select"
              >
                <option value="">Unit</option>
                <option value="hours">hrs</option>
                <option value="days">days</option>
                <option value="units">units</option>
                <option value="each">each</option>
              </select>
            </div>
            {editQuantity && editUnitPrice && (() => {
              const calculatedAmount = parseFloat(editQuantity) * parseFloat(editUnitPrice);
              const mismatch = Math.abs(calculatedAmount - editAmount) > 0.01;
              return (
                <p className={`text-xs ${mismatch ? 'text-amber-600' : 'text-muted-foreground'}`}>
                  = ${calculatedAmount.toFixed(2)}
                  {mismatch && <span className="ml-1">(≠ amount)</span>}
                </p>
              );
            })()}
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div className="space-y-1">
            <Input
              type="number"
              value={editAmount}
              onChange={(e) => setEditAmount(parseFloat(e.target.value) || 0)}
              onKeyDown={handleKeyDown}
              data-testid="edit-amount-input"
              className="w-24 text-right ml-auto"
              step="0.01"
              min="0"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              This invoice only
              {isPartialInvoice && ` (of $${billableTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })} total)`}
            </p>
            {needsLowerChoice && !isOverBillableAmount && (
              <LowerItemTotalChoice
                lineAmount={editAmount}
                outcome={outcome}
                value={lowerChoice}
                onChange={setLowerChoice}
                note={lowerChoiceNote}
              />
            )}
          </div>
        </TableCell>
        <TableCell className="text-right">
          {/* v0.14.4 R2: editable per-line tax rate. Empty = keep current rate;
              explicit 0 overrides to no tax. Category suggestion shown as a hint
              when it differs from the current rate. */}
          <div className="space-y-1">
            <div className="flex items-center justify-end">
              <Input
                type="number"
                value={editTaxRate}
                onChange={(e) => setEditTaxRate(e.target.value)}
                onKeyDown={handleKeyDown}
                data-testid="edit-tax-rate-input"
                className="w-20 text-right ml-auto"
                step="0.01"
                min="0"
                max="100"
                placeholder={showSuggestion ? suggestedRate.toFixed(2) : undefined}
                aria-label="Tax rate percent"
              />
              <span className="ml-1 text-muted-foreground">%</span>
            </div>
            {showSuggestion && (
              <p className="text-xs text-muted-foreground" data-testid="tax-rate-suggestion">
                Suggested: {suggestedRate.toFixed(2)}%
              </p>
            )}
            {!isRateInRange && (
              <p className="text-xs text-destructive" data-testid="tax-rate-range-error">
                0–100 only
              </p>
            )}
          </div>
        </TableCell>
        <TableCell>{taxClassCell}</TableCell>
        <TableCell className="text-right">
          <span data-testid="derived-tax-amount">
            ${calculatedTax.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            {isOverBillableAmount ? (
              <>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveAmount}
                  disabled={updateInvoiceItem.isPending || updateBillableItem.isPending || !isRateInRange}
                  data-testid="save-amount-and-update-billable-item-button"
                  className="h-8 px-2 text-xs"
                >
                  Save & update billable item to ${newBillableItemAmount.toLocaleString(
                    'en-US',
                    { minimumFractionDigits: 0, maximumFractionDigits: 2 }
                  )}
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertTriangle
                        className="h-4 w-4 text-amber-500 cursor-help"
                        data-testid="billable-item-update-warning"
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Billable item amount will change.</p>
                      {otherInvoiceCount > 0 && (
                        <p>
                          {otherInvoiceCount} other invoice(s) use this item — their amounts won't change.
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSaveAmount}
                disabled={updateInvoiceItem.isPending || !isRateInRange || lowerChoiceMissing}
                data-testid="save-amount-button"
                className="h-8 w-8 p-0"
              >
                <Check className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              data-testid="cancel-edit-button"
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  // Phase 9.34: Check if quantity/rate info is present
  const hasQuantityRate = item.quantity != null && item.unit_price != null;

  return (
    <TableRow>
      <TableCell>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {/* Phase 9.27: Display billable_item_description (current, not snapshot) */}
            {displayDescription}
            {/* Phase 10b.2: Receipt indicator for expense items with attached receipts */}
            {item.receipt_count != null && item.receipt_count > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex items-center gap-0.5 text-amber-600 cursor-help"
                      data-testid="receipt-indicator"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">{item.receipt_count}</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{item.receipt_count} receipt{item.receipt_count !== 1 ? 's' : ''} attached</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {isOnMultipleInvoices && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle
                      className="h-4 w-4 text-amber-500 cursor-help"
                      data-testid="multi-invoice-warning"
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This item appears on {item.invoice_count} invoices.</p>
                    <p>{otherInvoiceHint}</p>
                    <p>Editing amount only affects this invoice.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {/* Phase 9.34: Display quantity/rate when present */}
          {hasQuantityRate && (
            <div className="text-xs text-muted-foreground" data-testid="quantity-rate-display">
              {item.quantity} {item.unit_type ?? 'units'} × ${item.unit_price?.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex flex-col items-end">
          <span>
            ${item.amount.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          {/* Phase 9.27: Show "of $X total" for partial invoices */}
          {isPartialInvoice && (
            <span className="text-xs text-muted-foreground" data-testid="partial-invoice-context">
              of ${billableTotal.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">{item.tax_rate.toFixed(2)}%</TableCell>
      <TableCell>{taxClassReadOnlyCell}</TableCell>
      <TableCell className="text-right">
        ${item.tax_amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </TableCell>
      {isStatusEditable ? (
        <TableCell>
          <div className="flex gap-1">
            {canEdit && (
              <>
                {/* Phase 9.27: Separate edit buttons for description (cascades) and amount (per-invoice).
                    R1 (SPEC-v017-completion-2026-09-27, AC3): both buttons used to be icon-only, with the
                    label living ONLY inside a Radix tooltip that mounts on hover/focus -- her report
                    ("would not let me just edit the number") is the SECOND occurrence of this exact
                    discoverability gap (KB §3.6e, "it won't even let me save the invoice", 2026-08-10).
                    Adding a tooltip was already tried and did not fix it; the label now renders as part
                    of the button itself, visible with no interaction, same idiom as the "Remove" button
                    beside them. Tooltips stay for the EXTRA context (cascade warning / invoice-only scope). */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Sync state from current props before entering edit mode
                          // to avoid showing stale values from previous edits
                          setEditDescription(displayDescription);
                          setEditMode('description');
                        }}
                        data-testid="edit-description-button"
                        className="h-8 px-2 gap-1 text-xs"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Description
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Edit description</p>
                      {otherInvoiceHint && (
                        <p className="text-xs text-amber-200">{otherInvoiceHint}</p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Sync state from current props before entering edit mode
                          // to avoid showing stale values from previous edits
                          setEditAmount(item.amount);
                          setEditQuantity(item.quantity?.toString() ?? '');
                          setEditUnitPrice(item.unit_price?.toString() ?? '');
                          setEditUnitType(item.unit_type ?? '');
                          // v0.14.4 R2: prefill the tax-rate input with the
                          // current rate before entering edit mode.
                          setEditTaxRate(item.tax_rate?.toString() ?? '');
                          setEditMode('amount');
                        }}
                        data-testid="edit-amount-button"
                        className="h-8 px-2 gap-1 text-xs text-blue-600 hover:text-blue-700"
                      >
                        <DollarSign className="h-3.5 w-3.5" />
                        Amount
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Edit amount for this invoice</p>
                      <p className="text-xs text-muted-foreground">Only affects this invoice</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(item.id)}
              className="text-destructive hover:text-destructive"
            >
              Remove
            </Button>
          </div>
        </TableCell>
      ) : (
        // R4 (christa-reviewer, iteration 3): the edit/remove controls above
        // render nothing at all once the invoice leaves draft/ready — that
        // silence is what she experienced as having no option to fix a typo
        // (invoice 602, 2026-06-30: "it doesn't give me an option to just fix
        // a typo" — see R4-repro-download-and-typo.md line 29). Replace the
        // silent empty cell with the reason, without changing the lock itself
        // (see routers/invoice_items.py::_raise_if_invoice_locked, unchanged
        // by this fix).
        <TableCell>
          <p className="text-xs text-muted-foreground" data-testid="items-locked-message">
            Line items lock once an invoice is sent.
          </p>
        </TableCell>
      )}
    </TableRow>
  );
}
